import { formatUnits, parseUnits } from '@ethersproject/units';
import { useUseLcroTour } from '@ui/components/Tours';
import type { TransactionStatus } from '@ui/components/TransactionDrawer';
import useDebounce from '@ui/hooks/useDebounce';
import useFarmAprs from '@ui/hooks/useFarmAprs';
import useGardenApy from '@ui/hooks/useGardenApy';
import useLpAmountsStaked from '@ui/hooks/useLpAmountsStaked';
import useLpTokenBalance from '@ui/hooks/useLpTokenBalance';
import useLpUsdValue from '@ui/hooks/useLpUsdValue';
import usePendingVno from '@ui/hooks/usePendingVno';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { parseValue } from '@ui/utils/format';
import type { GardenToken, GardenType } from '@ui/utils/garden';
import { gardenTokenDict, getLpUsdValue } from '@ui/utils/garden';
import { useCallback, useEffect, useState } from 'react';

import { useGardenPlantDrawer } from './GardenPlantDrawer/useGardenPlantDrawer';
import { useGardenModalStore } from './useGardenModalStore';

const useGardenViewPlant = (gardenType: GardenType, isWithdraw?: boolean) => {
  const t = useTranslations();
  const coinMode = useGardenModalStore((s) => s.coinMode);
  const gardenTokenInfo = gardenTokenDict[coinMode][gardenType] as Extract<
    GardenToken,
    { enabled: true }
  >;
  const assetName = gardenTokenInfo.name;
  const openGardenPlantDrawer = useGardenPlantDrawer((s) => s.onOpen);
  const { pendingVno } = usePendingVno(gardenType);
  const [lpUsdWorth, setLpUsdWorth] = useState<number | undefined>(undefined);
  const [transactionStatus, setTransactionStatus] =
    useState<TransactionStatus>('CONFIRM');
  const [inputError, setInputError] = useState('');
  const sdk = useVenoSDK();
  const [enteredAmount, setEnteredAmount] = useState('');
  const debouncedEntered = useDebounce(enteredAmount, 500);
  useState<TransactionStatus>('CONFIRM');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { stakedFerro, stakedTectonic, stakedVvs, stakedVvsCro, stakedVvsTia } =
    useLpAmountsStaked(gardenType);
  const { isBoosted } = useFarmAprs();

  const [mode, setMode] = useState<'DEPOSIT' | 'WITHDRAW'>(
    isWithdraw ? 'WITHDRAW' : 'DEPOSIT',
  );

  const { balance } = useLpTokenBalance(coinMode, gardenType);

  let staked = stakedFerro;
  switch (gardenType) {
    case 'VVS':
      staked = stakedVvs;
      break;
    case 'VVS_CRO':
      staked = stakedVvsCro;
      break;
    case 'VVS_TIA':
      staked = stakedVvsTia;
      break;
    case 'TECTONIC':
      staked = stakedTectonic;
      break;
  }

  const maxAmountDeposit = balance
    ? bigNumberToNumber(balance, gardenTokenInfo.decimals)
    : undefined;

  const maxAmountWithdraw = staked
    ? bigNumberToNumber(staked, gardenTokenInfo.decimals)
    : undefined;

  const maxAmount = mode === 'DEPOSIT' ? maxAmountDeposit : maxAmountWithdraw;

  const onModeChange = useCallback((mode: 'DEPOSIT' | 'WITHDRAW') => {
    setMode(mode);
    setInputError('');
    setEnteredAmount('');
  }, []);

  const {
    ferroGardenApy,
    tectonicGardenApy,
    vvsGardenApy,
    vvsCroGardenApy,
    vvsTiaGardenApy,
  } = useGardenApy(gardenType);

  let gardenApy = ferroGardenApy ?? 0;
  if (gardenType === 'TECTONIC') gardenApy = tectonicGardenApy ?? 0;
  if (gardenType === 'VVS') gardenApy = vvsGardenApy ?? 0;
  if (gardenType === 'VVS_CRO') gardenApy = vvsCroGardenApy ?? 0;
  if (gardenType === 'VVS_TIA') gardenApy = vvsTiaGardenApy ?? 0;

  useEffect(() => {
    const getUsdValue = async () => {
      const usdValue =
        debouncedEntered && !inputError
          ? await getLpUsdValue(
              coinMode,
              gardenType,
              sdk,
              parseUnits(debouncedEntered),
            )
          : undefined;
      setLpUsdWorth(usdValue ? parseFloat(formatUnits(usdValue)) : undefined);
    };
    getUsdValue();
  }, [
    coinMode,
    debouncedEntered,
    gardenTokenInfo.decimals,
    gardenType,
    inputError,
    sdk,
  ]);

  const stakedUsd = useLpUsdValue(
    gardenType,
    staked ? formatUnits(staked, gardenTokenInfo.decimals) : '',
  );
  const balanceUsd = useLpUsdValue(
    gardenType,
    balance ? formatUnits(balance, gardenTokenInfo.decimals) : '',
  );

  const handleInputChange = useCallback(
    (value: string) => {
      if (value && !/^[0-9]+\.?[0-9]*$/.test(value)) {
        setInputError(t('invalid format'));
      } else if (maxAmount !== undefined && parseFloat(value) > maxAmount) {
        setInputError(
          mode === 'DEPOSIT'
            ? t('{coin} balance too low', {
                coin: assetName,
              })
            : t('{coin} deposit too low', {
                coin: assetName,
              }),
        );
      } else {
        setInputError('');
        useUseLcroTour.setState({
          inputGardenAmountStr: value,
        });
      }
      setEnteredAmount(value);
    },
    [maxAmount, t, assetName, mode],
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const reset = useCallback(() => {
    closeDrawer();
    setTransactionStatus('CONFIRM');
    setInputError('');
    setEnteredAmount('');
  }, [closeDrawer]);

  const onClickCTA = async () => {
    openGardenPlantDrawer({
      coinMode,
      isWithdraw: mode === 'WITHDRAW',
      plantDirectly: pendingVno && pendingVno.lte(0),
      gardenType,
      tLcroPlantAmount: parseValue(enteredAmount, gardenTokenInfo.decimals),
    });
    reset();
  };

  return {
    maxAmount,
    gardenApy,
    handleInputChange,
    assetName,
    onClickCTA,
    enteredAmount,
    inputError,
    transactionStatus,
    drawerOpen,
    closeDrawer,
    reset,
    onModeChange,
    mode,
    balance,
    setDrawerOpen,
    staked,
    isBoosted,
    lpUsdWorth,
    stakedUsd,
    balanceUsd,
  };
};

export default useGardenViewPlant;
